import { useEffect, useMemo, useState } from "react"
import type { ColumnDef, PaginationState } from "@tanstack/react-table"

import { DataTable } from "@/components/DataTable"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  posadaDisplay,
  type OtkSubjectResponsiblePerson,
} from "@/lib/api/subjects"
import { useSession } from "@/store/session"

type ResponsiblePersonRow = {
  certSerial: string
  name: string
  posada: string
}

// `GET /api/otk/subject` (subjects.ts's `OtkSubjectProfile.responsiblePersons`)
// carries `rnokpp`/`certificateSerial`/`prizvyshche`/`imia`/`poBatkovi`/
// `posada` — no `punktId`, `status`, or `certificateValidTo`, and no `armRole` at all
// (subject-responsible.md's own Grounding note says that field is grounded in
// the workstation access model, not this registry entity). "Пункт ОТК",
// "Статус", and "Роль в АРМ" render "—" until a dedicated
// `/responsible-persons` endpoint exposing the fuller `vidpovidalna-osoba.md`
// shape exists; "Сертифікат КЕП" renders just the serial, with no
// "· до <date>" suffix, since no expiry date is available here.
function toRow(person: OtkSubjectResponsiblePerson): ResponsiblePersonRow {
  return {
    certSerial: person.certificateSerial,
    name: [person.prizvyshche, person.imia, person.poBatkovi]
      .filter(Boolean)
      .join(" "),
    posada: posadaDisplay[person.posada] ?? person.posada,
  }
}

/**
 * subject-responsible.md's `/subject/responsible` — read-only per
 * `vidpovidalna-osoba.md`'s rule 1, so no actions column and no write call
 * anywhere on this page. `RestrictedPaymentNotice` is intentionally not
 * wired here for the same reason `SubjectRoles.tsx`/`SubjectProfile.tsx`
 * omit it: this app has no `DemoScenariosPanel` equivalent, so the
 * "Доступ в обмеженому режимі" trigger the wireframe used doesn't exist here.
 *
 * Sourced from the session store's `subject.responsiblePersons` (the same
 * `GET /subject` call `SubjectProfile.tsx` and `AppShell` already share) —
 * see `toRow`'s comment above for exactly which columns that shape can't
 * back yet — rather than a dedicated `/responsible-persons` endpoint, which
 * `otk-be` doesn't have.
 */
export function SubjectResponsible() {
  const subject = useSession((s) => s.subject)
  const fetchSubject = useSession((s) => s.fetchSubject)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  useEffect(() => {
    let cancelled = false
    fetchSubject()
      .catch((err) => {
        if (cancelled) return
        const code = err?.response?.data?.error as string | undefined
        setError(
          code === "SUBJECT_NOT_CONNECTED"
            ? "Обліковий запис ще не пов'язаний із суб'єктом ОТК."
            : "Не вдалося завантажити список відповідальних осіб."
        )
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [fetchSubject])

  const rows = useMemo(
    () => (subject?.responsiblePersons ?? []).map(toRow),
    [subject]
  )

  const columns = useMemo<ColumnDef<ResponsiblePersonRow>[]>(
    () => [
      { accessorKey: "name", header: "ПІБ", meta: { className: "w-64" } },
      {
        id: "punkt",
        header: "Пункт ОТК",
        meta: { className: "w-36" },
        cell: () => <span className="text-muted-foreground">—</span>,
      },
      {
        id: "status",
        header: "Статус",
        meta: { className: "w-32" },
        cell: () => <span className="text-muted-foreground">—</span>,
      },
      { accessorKey: "posada", header: "Посада", meta: { className: "w-56" } },
      {
        id: "armRole",
        header: "Роль в АРМ",
        meta: { className: "w-72" },
        cell: () => <span className="text-muted-foreground">—</span>,
      },
      { accessorKey: "certSerial", header: "Сертифікат КЕП" },
    ],
    []
  )

  return (
    <>
      <h1 className="mb-6 text-2xl font-semibold">
        Суб'єкт ОТК — Відповідальні особи
      </h1>

      {isLoading && (
        <p className="text-sm text-muted-foreground">Завантаження…</p>
      )}
      {!isLoading && error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {!isLoading && !error && subject && (
        <Card>
          <CardHeader>
            <CardTitle>Відповідальні особи</CardTitle>
            <CardDescription>
              Дані вносяться ГСЦ МВС; редагування списку в АРМ недоступне.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={columns}
              data={rows}
              getRowId={(row) => row.certSerial}
              onPaginationChange={setPagination}
              pagination={pagination}
              tableClassName="table-fixed"
            />
          </CardContent>
        </Card>
      )}
    </>
  )
}
